import React, {useEffect, useRef, useState} from 'react';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../api';

const statusOf = (expirationDate) => {
  if (!expirationDate) return {label: 'No Expiration', cls: 'bg-gray-100 text-gray-600'};
  const days = Math.ceil((new Date(expirationDate + 'T00:00:00') - new Date()) / 86400000);
  if (days < 0) return {label: 'Expired', cls: 'bg-red-100 text-red-800'};
  if (days <= 30) return {label: `Expires in ${days}d`, cls: 'bg-amber-100 text-amber-800'};
  if (days <= 90) return {label: `Expires in ${days}d`, cls: 'bg-yellow-100 text-yellow-800'};
  return {label: 'Active', cls: 'bg-green-100 text-green-800'};
};

const empty = {name: '', issuingOrganization: '', credentialNumber: '', issueDate: '', expirationDate: '', notes: ''};

export default function MyCredentials() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null=closed, {id?,...}=editing/creating
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef();

  const load = () => {
    api('/hcm/my-credentials').then(r => r.ok ? r.json() : []).then(setCredentials).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {name: form.name, issuingOrganization: form.issuingOrganization || null, credentialNumber: form.credentialNumber || null,
      issueDate: form.issueDate || null, expirationDate: form.expirationDate || null, notes: form.notes || null};
    const res = form.id
      ? await api(`/hcm/my-credentials/${form.id}`, {method: 'PUT', body: JSON.stringify(body)})
      : await api('/hcm/my-credentials', {method: 'POST', body: JSON.stringify(body)});
    if (res.ok) {
      const cred = await res.json();
      if (form.file) {
        const fd = new FormData();
        fd.append('file', form.file);
        const headers = {};
        const jwt = getCookie('jwtToken'); if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
        const tid = getCookie('tenantId'); if (tid) headers['X-Tenant-Id'] = tid;
        await fetch(`${API_BASE}/hcm/my-credentials/${cred.id}/file`, {method: 'POST', headers, body: fd});
      }
      setForm(null);
      load();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    await api(`/hcm/my-credentials/${deleting.id}`, {method: 'DELETE'});
    setDeleting(null);
    load();
  };

  const handleDownload = async (id) => {
    const res = await api(`/hcm/my-credentials/${id}/file-url`);
    if (res.ok) { const d = await res.json(); window.open(d.url, '_blank'); }
  };

  if (loading) return <Layout><div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div></Layout>;

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Credentials</h2>
        <button onClick={() => setForm({...empty})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Credential</button>
      </div>

      {credentials.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow"><p className="text-gray-500">No credentials yet. Add your first credential to get started.</p></div>
      ) : (
        <div className="space-y-4">
          {credentials.map(c => {
            const st = statusOf(c.expirationDate);
            return (
              <div key={c.id} className="bg-white rounded-lg shadow p-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    </div>
                    {c.issuingOrganization && <p className="text-sm text-gray-600">{c.issuingOrganization}</p>}
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-500">
                      {c.credentialNumber && <span>#{c.credentialNumber}</span>}
                      {c.issueDate && <span>Issued: {c.issueDate}</span>}
                      {c.expirationDate && <span>Expires: {c.expirationDate}</span>}
                    </div>
                    {c.notes && <p className="text-sm text-gray-500 mt-2 italic">{c.notes}</p>}
                    {c.fileName && (
                      <button onClick={() => handleDownload(c.id)} className="text-sm text-indigo-600 hover:text-indigo-800 mt-2 inline-flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>
                        {c.fileName}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <button onClick={() => setForm({id: c.id, name: c.name, issuingOrganization: c.issuingOrganization || '', credentialNumber: c.credentialNumber || '', issueDate: c.issueDate || '', expirationDate: c.expirationDate || '', notes: c.notes || ''})}
                      className="text-sm text-gray-500 hover:text-indigo-600">Edit</button>
                    <button onClick={() => setDeleting(c)} className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{form.id ? 'Edit Credential' : 'Add Credential'}</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credential Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. CPR Certification" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Organization</label>
                <input value={form.issuingOrganization} onChange={e => setForm({...form, issuingOrganization: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. American Red Cross" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credential Number</label>
                <input value={form.credentialNumber} onChange={e => setForm({...form, credentialNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                <input type="date" value={form.issueDate} onChange={e => setForm({...form, issueDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                <input type="date" value={form.expirationDate} onChange={e => setForm({...form, expirationDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
              <input ref={fileRef} type="file" onChange={e => setForm({...form, file: e.target.files[0]})}
                className="text-sm text-gray-600" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={saving || !form.name.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setDeleting(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Credential</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deleting.name}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
